import { money, titleize, shortDate } from "../../lib/api";

const F = ({ l, v, testId }) => (
  <div className="grid grid-cols-[minmax(140px,38%)_1fr] gap-3 border-b border-slate-100 py-1.5 text-sm" data-testid={testId}>
    <span className="font-semibold text-slate-500">{l}</span><span className="text-slate-900">{v ?? "—"}</span>
  </div>
);
const join = (x) => (Array.isArray(x) ? x.map(titleize).join(", ") : titleize(x)) || "—";

export const SituationReport = ({ r }) => {
  const p = r.property || {};
  return (
    <div className="print-area rounded-2xl border border-slate-200 bg-white p-6 text-slate-900 sm:p-8" data-testid="situation-report">
      <div className="flex flex-wrap items-end justify-between gap-2 border-b-2 border-slate-900 pb-3">
        <div><img src="/brand/logo.webp" alt="AskJoe-AI.com" className="mb-1 h-12 w-auto" /><h2 className="font-display text-3xl font-extrabold uppercase text-brand-navy">Homeowner Situation Report</h2></div>
        <p className="font-mono text-xs text-slate-500">Generated {shortDate(r.generated_at)} • Preliminary</p>
      </div>
      <div className="mt-4 grid gap-x-8 md:grid-cols-2">
        <div>
          <F l="Lead ID" v={<span className="font-mono">{r.lead_id}</span>} testId="report-lead-id" />
          <F l="Referral ID" v={<span className="font-mono">{r.referral_id}</span>} testId="report-referral-id" />
          <F l="Affiliate/Team Member" v={r.partner?.name} />
          <F l="Campaign" v={r.campaign} />
          <F l="QR Code" v={<span className="font-mono">{r.qr_code || "—"}</span>} />
          <F l="Original Lead Source" v={titleize(r.original_source)} />
          <F l="Property" v={p.street ? `${p.street}, ${p.city || ""} ${p.state || ""} ${p.zip || ""} (${p.county || "—"} County)` : "—"} />
          <F l="Homeowner" v={r.homeowner} />
          <F l="Primary Goal" v={join(r.primary_goal)} />
          <F l="Primary Problem" v={join(r.primary_problem)} />
          <F l="Mortgage Status" v={r.mortgage_status} />
          <F l="Foreclosure Stage" v={r.foreclosure_stage} />
          <F l="Property Tax Status" v={titleize(r.tax_status)} />
          <F l="Tax Deed Status" v={["TAX_DEED_APPLICATION", "TAX_DEED_SALE_SCHEDULED"].includes(r.tax_status) ? titleize(r.tax_status) : "None reported"} />
          <F l="Property Condition" v={titleize(r.property_condition)} />
        </div>
        <div>
          <F l="Est. Property Value" v={money(r.equity.value)} />
          <F l="Known Mortgage Debt" v={money((r.equity.first_mortgage || 0) + (r.equity.second_mortgage || 0) + (r.equity.heloc || 0))} />
          <F l="Known Other Liens" v={money((r.equity.tax_liens || 0) + (r.equity.hoa_liens || 0) + (r.equity.other_liens || 0) + (r.equity.property_taxes_owed || 0))} />
          <F l="Est. Gross Equity" v={money(r.equity.gross)} testId="report-gross-equity" />
          <F l="Est. Net Sale Equity" v={money(r.equity.net)} />
          <F l="Monthly Income" v={r.cashflow.known ? money(r.cashflow.income) : "—"} />
          <F l="Monthly Expenses" v={r.cashflow.known ? money(r.cashflow.expenses) : "—"} />
          <F l="Monthly Surplus/Deficit" v={r.cashflow.known ? money(r.cashflow.surplus) : "—"} />
          <F l="Urgency Classification" v={`${r.urgency.color} — ${r.urgency.label}`} testId="report-urgency" />
          <F l="Legal Review Priority" v={r.scorecard.legal_review_priority} />
          <F l="Recommended Specialist" v={r.specialists.map((s) => s.label).join(", ")} />
        </div>
      </div>
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <Block t="Important Dates" items={r.important_dates.map((e) => `${e.label}: ${e.date}${e.days_remaining !== null ? ` (${e.days_remaining} days)` : ""}`)} />
        <Block t="Documents Received" items={r.documents_received} />
        <Block t="Documents Missing" items={r.documents_missing} />
        <Block t="Conflicting Information" items={r.conflicts.map((c) => c.message)} />
        <Block t="Information Requiring Verification" items={r.verification.filter((v) => v.status !== "CONFIRMED").map((v) => `${v.label} — ${titleize(v.status)}`)} />
        <Block t="Potential Solution Paths" items={r.solutions} />
        <Block t="Recommended Next Actions" items={r.next_actions} />
      </div>
      <p className="mt-6 border-t border-slate-200 pt-3 text-[11px] leading-relaxed text-slate-500">All amounts are ESTIMATES until verified. AskJoe-AI is not a law firm and this report is not legal, tax or financial advice. Urgency colors and scores are preliminary organizing tools, not legal conclusions. Legal response deadlines are not calculated by AskJoe-AI.</p>
    </div>
  );
};

const Block = ({ t, items }) => (
  <div><p className="font-display text-sm font-bold uppercase tracking-wide">{t}</p>
    {items?.length ? <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">{items.map((i, n) => <li key={n}>{i}</li>)}</ul> : <p className="mt-1 text-sm text-slate-500">None</p>}
  </div>
);
